import { Head } from "@inertiajs/react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  hairbookPostHlsUrl,
  hairbookPostThumbnailR2Url,
  hairbookSalonUrl,
} from "@/lib/_url";
import { useVideoPlayer } from "@/lib/_use-video-player";

interface PostItem {
  id: string;
  salonId?: string | number | null;
  staffProfileId?: string | number | null;
  hpbSalonId?: string | null;
  hpbStaffId?: string | null;
  publishedAt?: string | null;
  videoOrientation?: string | null;
  aspectRatio?: string | null;
}

interface Meta {
  title: string;
  description: string;
}

interface Props {
  posts?: PostItem[];
  meta?: Meta;
}

export default function PostsIndex({ posts = [], meta }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const safePosts = useMemo(() => posts.filter((post) => post.id), [posts]);

  return (
    <>
      <Head>
        <title>{meta?.title ?? "Hairbook Swipe"}</title>
        <meta
          name="description"
          content={meta?.description ?? "Hairbookの動画投稿をスワイプで見るページです。"}
        />
      </Head>

      <main className="min-h-screen bg-zinc-950 text-white">
        {safePosts.length === 0 ? (
          <EmptyFeed />
        ) : (
          <section className="mx-auto h-screen max-w-[448px] snap-y snap-mandatory overflow-y-auto bg-black">
            {safePosts.map((post, index) => (
              <SwipeCard
                key={post.id}
                post={post}
                index={index}
                isActive={index === activeIndex}
                onActive={setActiveIndex}
              />
            ))}
          </section>
        )}
      </main>
    </>
  );
}

function SwipeCard({
  post,
  index,
  isActive,
  onActive,
}: {
  post: PostItem;
  index: number;
  isActive: boolean;
  onActive: (index: number) => void;
}) {
  const cardRef = useRef<HTMLElement | null>(null);
  const videoUrl = hairbookPostHlsUrl(post.id);
  const posterUrl = hairbookPostThumbnailR2Url(post.id);
  const isLandscape = post.videoOrientation === "landscape" || post.aspectRatio === "16:9";
  const { videoRef, isLoading } = useVideoPlayer({
    src: videoUrl,
    poster: posterUrl,
    paused: !isActive,
    muted: true,
    loop: true,
  });

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && entry.intersectionRatio >= 0.65) {
          onActive(index);
        }
      },
      { threshold: [0.65] },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [index, onActive]);

  return (
    <article
      ref={cardRef}
      className="relative flex h-screen snap-start snap-always items-center justify-center overflow-hidden bg-black"
    >
      <video
        ref={videoRef}
        poster={posterUrl}
        playsInline
        muted
        loop
        preload={isActive ? "auto" : "metadata"}
        className={`h-full w-full ${isLandscape ? "object-contain" : "object-cover"}`}
      />

      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/55 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent px-4 pb-6 pt-20">
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-bold text-white/60">Hairbook Swipe</p>
            <h1 className="mt-1 truncate text-lg font-bold tracking-tight">
              {post.hpbSalonId ? `Salon ${post.hpbSalonId}` : `Post ${post.id}`}
            </h1>
            <p className="mt-2 text-xs font-medium text-white/70">
              {publishedDateLabel(post.publishedAt)}
              {post.videoOrientation ? ` / ${post.videoOrientation}` : ""}
            </p>
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            {post.hpbSalonId && (
              <a
                href={hairbookSalonUrl(post.hpbSalonId)}
                className="pointer-events-auto inline-flex h-10 items-center justify-center rounded-full border border-white/25 bg-white/12 px-4 text-xs font-bold text-white backdrop-blur"
              >
                サロン
              </a>
            )}
            <a
              href={videoUrl}
              className="pointer-events-auto inline-flex h-10 items-center justify-center rounded-full border border-white/25 bg-white/12 px-4 text-xs font-bold text-white backdrop-blur"
            >
              動画
            </a>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center bg-black/20">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/25 border-t-white" />
        </div>
      )}
    </article>
  );
}

function EmptyFeed() {
  return (
    <section className="grid min-h-screen place-items-center px-6 text-center">
      <div>
        <p className="text-sm font-bold text-white/70">Hairbook Swipe</p>
        <h1 className="mt-3 text-2xl font-bold tracking-tight">投稿がまだありません</h1>
        <p className="mt-3 text-sm leading-6 text-white/50">
          posts の同期とインポートが完了すると、ここに動画フィードが表示されます。
        </p>
      </div>
    </section>
  );
}

function publishedDateLabel(value?: string | null) {
  if (!value) return "公開日未設定";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}
